import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { ShieldCheck, ShieldAlert, Search, Loader2, Phone, Globe } from 'lucide-react';
import { API } from '../lib/api';

const STATUS = {
  active: ['Active', 'bg-emerald-50 text-emerald-700 border-emerald-200', ShieldCheck],
  expired: ['Expired', 'bg-amber-50 text-amber-700 border-amber-200', ShieldAlert],
  under_review: ['Under review', 'bg-sky-50 text-sky-700 border-sky-200', ShieldAlert],
  not_valid: ['Not valid', 'bg-rose-50 text-rose-700 border-rose-200', ShieldAlert],
};
const fmt = (d) => (d ? new Date(d).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) : '');
const apiOrigin = API.replace(/\/api\/?$/, '');

const Row = ({ label, value, testid }) => (value ? (
  <div className="flex justify-between gap-4 border-b border-slate-100 py-2.5 text-sm last:border-0">
    <span className="text-slate-500">{label}</span>
    <span className="text-right font-medium text-slate-900" data-testid={testid}>{value}</span>
  </div>
) : null);

const Result = ({ w }) => {
  const [label, cls, Icon] = STATUS[w.status] || STATUS.under_review;
  return (
    <div className="mt-5 rounded-2xl border border-slate-200 bg-white p-5" data-testid="verify-result">
      <div className="flex items-center justify-between gap-3">
        <span className="font-mono text-sm font-semibold text-slate-900" data-testid="verify-warranty-id">{w.warranty_id}</span>
        <span className={`inline-flex items-center gap-1.5 rounded-full border px-3 py-1 text-xs font-semibold ${cls}`} data-testid="verify-status">
          <Icon className="h-3.5 w-3.5" />{label}
        </span>
      </div>
      <div className="mt-3">
        <Row label="Valid until" value={w.lifetime ? 'Lifetime' : fmt(w.valid_until)} testid="verify-valid-until" />
        <Row label="Vehicle" value={w.vehicle} testid="verify-vehicle" />
        <Row label="Registration" value={w.vehicle_registration} testid="verify-registration" />
        <Row label="Service" value={w.service} testid="verify-service" />
        <Row label="Product" value={w.product} testid="verify-product" />
        <Row label="Coverage" value={w.coverage_area} testid="verify-coverage" />
        <Row label="Customer" value={w.customer} testid="verify-customer" />
      </div>
    </div>
  );
};

export default function WarrantyVerify() {
  const { tenantId } = useParams();
  const [params] = useSearchParams();
  const embed = params.get('embed') === '1';
  const [q, setQ] = useState(params.get('id') || '');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const root = useRef(null);

  const lookup = useCallback(async (id) => {
    setLoading(true); setError('');
    try {
      const { data: d } = await axios.get(`${API}/public/warranty-verify/${tenantId}`, { params: { id: id || '' } });
      setData(d);
    } catch { setError('This verification link is not valid.'); }
    finally { setLoading(false); }
  }, [tenantId]);

  useEffect(() => { lookup(params.get('id') || ''); }, [lookup, params]);

  useEffect(() => {
    if (!embed || !root.current || !window.ResizeObserver) return undefined;
    const ro = new ResizeObserver(() => window.parent.postMessage({ dlWarrantyHeight: root.current.scrollHeight + 8 }, '*'));
    ro.observe(root.current);
    return () => ro.disconnect();
  }, [embed]);

  const ws = data?.workshop;
  return (
    <div className={embed ? 'bg-transparent' : 'min-h-screen bg-slate-50 px-4 py-10'} data-testid="warranty-verify-page">
      <div ref={root} className="mx-auto max-w-md">
        {!embed && ws && (
          <div className="mb-6 text-center" data-testid="verify-workshop">
            {ws.logo_url && <img src={`${apiOrigin}${ws.logo_url}`} alt={ws.name} className="mx-auto mb-3 max-h-16 object-contain" />}
            <h1 className="text-xl font-bold text-slate-900">{ws.name}</h1>
            <p className="text-sm text-slate-500">eWarranty verification</p>
          </div>
        )}
        <form onSubmit={(e) => { e.preventDefault(); lookup(q.trim()); }} className="flex gap-2" data-testid="verify-form">
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Enter Warranty ID" className="flex-1 rounded-xl border border-slate-300 bg-white px-4 py-2.5 text-sm uppercase outline-none focus:border-teal-600" data-testid="verify-input" />
          <button type="submit" className="inline-flex items-center gap-1.5 rounded-xl bg-teal-700 px-4 py-2.5 text-sm font-semibold text-white transition-colors hover:bg-teal-800" data-testid="verify-submit">
            {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Search className="h-4 w-4" />}Verify
          </button>
        </form>
        {error && <p className="mt-4 text-sm text-rose-600" data-testid="verify-error">{error}</p>}
        {data?.not_found && <p className="mt-4 text-sm text-rose-600" data-testid="verify-not-found">No warranty found with this ID at {ws?.name}.</p>}
        {data?.warranty && <Result w={data.warranty} />}
        {!embed && ws && (ws.phone || ws.website) && (
          <div className="mt-6 flex flex-wrap justify-center gap-4 text-sm text-slate-600">
            {ws.phone && <a href={`tel:${ws.phone}`} className="inline-flex items-center gap-1.5 hover:text-teal-700" data-testid="verify-phone"><Phone className="h-4 w-4" />{ws.phone}</a>}
            {ws.website && <a href={ws.website.startsWith('http') ? ws.website : `https://${ws.website}`} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1.5 hover:text-teal-700" data-testid="verify-website"><Globe className="h-4 w-4" />{ws.website}</a>}
          </div>
        )}
      </div>
    </div>
  );
}
